import classNames from 'classnames'
import React from 'react'
import { getOrganizationPath } from 'scenes/Dashboard/Dashboard.routes'
import { useCompanyInfo } from 'scenes/Dashboard/data/deals/hooks'
import { Organization } from 'scenes/Dashboard/data/organizations/types'
import { makeStyles } from 'tss-react/mui'
import { Link } from 'utils-next/routes'

import { Theme, Typography } from '@mui/material'
import { WithStyles } from '@mui/styles'
import _ from 'lodash'
import LinkedinBoxIcon from 'mdi-react/LinkedinIcon'
import FacebookBoxIcon from 'mdi-react/FacebookIcon'
import TwitterBoxIcon from 'mdi-react/TwitterIcon'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'

import { MdiReactIconComponentType } from 'mdi-react'

import {
  BasicOrganizationInfoProps,
  basicOrganizationInfoStyles,
} from '../OrganizationDetailsModal/components/OrganizationDetailsView/components/BasicOrganizationInfo/BasicOrganizationInfo.presentational'

export type DealCardCompanyNameProps = {
  className?: string
  style?: React.CSSProperties
  organization: Organization
  urls?: {
    linkedin?: string
    facebook?: string
    twitter?: string
    website?: string
  }
}

export const DealCardCompanyName = React.memo(({ urls, className, style, organization }: DealCardCompanyNameProps) => {
  const { classes } = useStyles()
  const { organizationName, organizationDomain, organizationId } = useCompanyInfo(organization)
  const companyName = organizationName || organizationDomain || '–'
  const companyNameWithId = `${companyName} (id: ${organizationId})`
  const urlsToDisplay = displayUrls(classes, urls)

  return (
    <div className={classNames(className, classes.organizationName)} style={style}>
      <div className={classes.alignedRow}>
        <Typography variant="subtitle1">
          <Link to={getOrganizationPath(Number(organizationId))} className={classes.title}>
            {companyNameWithId}
          </Link>
        </Typography>
      </div>
      <div>{urlsToDisplay}</div>
    </div>
  )
})

function displayUrls(
  classes: WithStyles<typeof basicOrganizationInfoStyles>['classes'],
  urls: BasicOrganizationInfoProps['urls']
) {
  if (!urls || _.isEmpty(urls)) {
    return null
  }

  const allUrlsUndefined = Object.values(urls).every((x) => !x)
  if (allUrlsUndefined) {
    return null
  }

  const displayCustomizedIconLink = getIconLink(classes.icon, classes.iconLabel)
  const { website, linkedin, twitter, facebook } = urls
  return (
    <>
      {displayCustomizedIconLink(linkedin, LinkedinBoxIcon)}
      {displayCustomizedIconLink(facebook, FacebookBoxIcon)}
      {displayCustomizedIconLink(twitter, TwitterBoxIcon)}
      {displayCustomizedIconLink(website, OpenInNewIcon)}
    </>
  )
}

// eslint-disable-next-line react/display-name
const getIconLink = (iconClassName?: string, iconContainerClassName?: string) => (
  url: string | undefined,
  Icon: MdiReactIconComponentType
) => {
  if (!url) {
    return null
  }
  return (
    <a target="_blank" href={url} rel="noopener noreferrer" className={iconContainerClassName}>
      <Icon className={iconClassName} />
    </a>
  )
}
DealCardCompanyName.displayName = 'DealCardCompanyName'

const useStyles = makeStyles()(({ palette, spacing }: Theme) => ({
  organizationName: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  alignedRow: {
    display: 'flex',
    alignItems: 'center',
  },
  iconLabel: {
    width: spacing(2),
    height: spacing(2),
    fill: palette.primary.main,
    color: palette.primary.main,
  },
  icon: {
    fill: palette.primary.main,
    color: palette.primary.main,
  },
  title: {
    color: palette.primary.main,
  },
}))
